import { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
// @mui
import io from 'socket.io-client';
import { useQueryClient } from 'react-query';

import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { getUserDetails } from '../../utils';
import { useQueryGetUpcomingMeetingsList } from '../../hooks/react-query/meetings';
import { useQueryGetPairedEdifiers } from '../../hooks/react-query/pairing';

import { useQueryGetNotifications } from '../../hooks/react-query/useData';
import { OnboardingTour } from '../../components/onboarding-tour';

//

//
import Header from './header';
import Nav from './nav';

// ----------------------------------------------------------------------

const APP_BAR_MOBILE = 64;
const APP_BAR_DESKTOP = 92;

const StyledRoot = styled('div')({
  display: 'flex',
  height: '100vh',
  minHeight: '100%',
  overflow: 'hidden',
});

const Main = styled('div', {
  shouldForwardProp: (prop) => prop !== 'showNav',
})(({ theme, showNav }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  overflowX: 'hidden',
  overflowY: showNav ? 'hidden' : 'auto',

  paddingLeft: showNav ? 0 : '35px',
  paddingRight: showNav ? 0 : '35px',
  paddingTop: showNav ? 0 : APP_BAR_MOBILE + 24,
  paddingBottom: showNav ? 0 : theme.spacing(10),
  [theme.breakpoints.up('lg')]: {
    paddingTop: showNav ? 0 : APP_BAR_DESKTOP + 24,
    paddingLeft: showNav ? 0 : theme.spacing(2),
    paddingRight: showNav ? 0 : theme.spacing(2),
  },
}));

// ----------------------------------------------------------------------

export default function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const [socketInstance, setSocketInstance] = useState(null);

  const [showNav, setShowNav] = useState(false);
  const [runTour, setRunTour] = useState(false);

  const userDetails = getUserDetails();

  const userRoles = userDetails?.totalRoles?.length
    ? userDetails.totalRoles
    : userDetails?.role
    ? [userDetails.role]
    : [];
  const {
    data: eventsData,
    refetch: refetchEventsData,
    isLoading,
  } = useQueryGetUpcomingMeetingsList(userDetails?.token, navigate, toast, userRoles);
  const queryClient = useQueryClient();

  const { data: notificationsData, refetch: refetchNotifications } = useQueryGetNotifications(
    toast,
    userDetails?.token,
    navigate,
    queryClient,
    1,
    50 // Default pagination size
  );

  const needsPairedEdifiers = userRoles.some((r) =>
    ['TEACHER', 'COACH'].includes(String(r || '').toUpperCase())
  );
  const pairedEdifiersPages = ['/dashboard/paired', '/dashboard/teachers', '/dashboard/find-edifiers', '/dashboard/user-profile'];
  const shouldFetchPairedEdifiers = pairedEdifiersPages.some((path) => location.pathname.startsWith(path));
  const {
    isFetching: isLoadingEdifiersPair,
    data,
    refetch: refetchEdifiersPair,
  } = useQueryGetPairedEdifiers(
    userDetails?.token,
    navigate,
    toast,
    userRoles,
    needsPairedEdifiers && shouldFetchPairedEdifiers
  );

  useEffect(() => {
    if (!socketInstance) {
      setSocketInstance(io.connect(process.env.REACT_APP_SERVER_URL, { query: { token: userDetails?.token } }));
    }

    socketInstance?.on('connect', () => {

      // Emitting a "send message" event
    });
  }, []);

  useEffect(() => {
    socketInstance?.on('notification', (message) => {
      toast(message?.message, {
        icon: '👏',
        // icon:'💬'
      });
      refetchNotifications();
    });
  }, [socketInstance]);

  // Check if user should see the onboarding tour
  useEffect(() => {
    const shouldShowTour = !userDetails?.hasCompletedTour;
    
    // Delay tour start to ensure DOM elements are fully loaded and positioned
    if (shouldShowTour) {
      // Wait for DOM to be fully rendered and elements to be positioned
      const checkElementsAndStartTour = () => {
        // Check if first tour target exists
        const firstTarget = document.querySelector('[data-tour="sidebar-dashboard"]');
        
        if (firstTarget) {
          setRunTour(true);
        } else {
          // If elements not ready, wait a bit more
          setTimeout(checkElementsAndStartTour, 500);
        }
      };
      
      const timer = setTimeout(checkElementsAndStartTour, 1500);
      
      return () => clearTimeout(timer);
    }
    
    return undefined;
  }, [userDetails?.hasCompletedTour]);

  const handleTourComplete = () => {
    setRunTour(false);
  };

  return (
    <StyledRoot>
      <Header onOpenNav={() => setOpen(true)} showNav={showNav} data={notificationsData} />

      <Nav openNav={open} onCloseNav={() => setOpen(false)} showNav={showNav} />

      <Main showNav={showNav}>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Outlet
            context={[
              showNav,
              setShowNav,
              eventsData,
              refetchEventsData,
              isLoading,
              isLoadingEdifiersPair,
              data,
              refetchEdifiersPair,
              socketInstance,
            ]}
          />
        </Box>
      </Main>

      {/* Onboarding Tour */}
      <OnboardingTour run={runTour} onComplete={handleTourComplete} />
    </StyledRoot>
  );
}
